/**
 * Pure helpers for the per-contact sent status shown next to each buyer on
 * the order (kanban 6095f425). Kept out of the .tsx for react-refresh.
 */
import type { ContactDelivery, NotificationKind } from '../../types/orderNotifications';

const KIND_NOUN: Record<NotificationKind, string> = {
  confirmation: 'Confirmation',
  invoice_copy: 'Invoice copy',
  confirmation_update: 'Confirmation update',
};

const SENDER_TEXT: Record<NonNullable<ContactDelivery['sender']>, string> = {
  sales_site: 'by sales site',
  portal: 'by portal',
};

/** "10/6 3:00 PM" in the viewer's local time; '' when unknown. */
export function shortWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${d.getMonth() + 1}/${d.getDate()} ${time}`;
}

/**
 * The one-liner next to the name:
 *   "Confirmation sent 10/6 3:00 PM by portal"
 *   "Confirmation not sent yet: order not submitted to Sage"
 *   "Invoice copy #0160001 sent 10/7 by sales site"
 */
export function deliveryLine(d: ContactDelivery): string {
  const noun = KIND_NOUN[d.kind];
  const inv = d.kind === 'invoice_copy' && d.invoice_no ? ` #${d.invoice_no}` : '';
  if (d.state === 'sent') {
    const when = shortWhen(d.sent_at);
    const by = d.sender ? ` ${SENDER_TEXT[d.sender]}` : '';
    return `${noun}${inv} sent${when ? ` ${when}` : ''}${by}`;
  }
  return `${noun}${inv} ${d.detail ?? d.state.replace('_', ' ')}`;
}

/** Hover text: the evidence line plus the exact timestamp when sent. */
export function deliveryTitle(d: ContactDelivery): string {
  const parts: string[] = [];
  if (d.state === 'sent' && d.sent_at) {
    const exact = new Date(d.sent_at);
    parts.push(`Sent ${Number.isNaN(exact.getTime()) ? d.sent_at : exact.toLocaleString()} to ${d.email}`);
  }
  if (d.evidence) parts.push(d.evidence);
  return parts.join('\n');
}

/** CSS tone for the status: good / bad / waiting / muted. */
export type DeliveryTone = 'sent' | 'failed' | 'pending' | 'muted';

export function deliveryTone(state: ContactDelivery['state']): DeliveryTone {
  switch (state) {
    case 'sent':
      return 'sent';
    case 'failed':
      return 'failed';
    case 'relay_pending':
    case 'skipped':
      return 'pending';
    default:
      return 'muted';
  }
}

/** Rows for one contact, confirmation first. */
export function deliveryFor(rows: ContactDelivery[], email: string): ContactDelivery[] {
  const key = email.trim().toLowerCase();
  const mine = rows.filter((r) => r.email.toLowerCase() === key);
  const order: NotificationKind[] = ['confirmation', 'invoice_copy'];
  return [...mine].sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
}

/**
 * Which rows to print inline. The confirmation always (it is the question the
 * desk asks); the invoice copy only once something happened to it or the
 * contact asked for it and it is due — a draft order's "no invoice yet" is noise.
 */
export function inlineRows(rows: ContactDelivery[]): ContactDelivery[] {
  return rows.filter((r) => {
    if (r.kind === 'confirmation') return r.state !== 'opted_out';
    return r.state === 'sent' || r.state === 'failed' || r.state === 'skipped' || r.state === 'relay_pending';
  });
}
